import { formatDistanceToNow } from 'date-fns';
import { Eye, EyeOff } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import {
  useDisableFlexConfig,
  useSaveFlexConfig,
  useSyncFlexNow,
  useTestFlexConnection,
} from '../../features/flex/mutations';
import { useFlexConfig } from '../../features/flex/queries';
import type { AccountDto } from '../../lib/api/endpoints';
import { Button } from '../ui/Button';
import { Card } from '../ui/Card';
import { Input } from '../ui/Input';
import { Label } from '../ui/Label';

function errorText(error: unknown): string {
  if (error instanceof Error) return error.message;
  return 'Request failed.';
}

function lastSyncText(input: {
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
}): string {
  if (!input.lastSyncAt && !input.lastSyncStatus) return 'Never synced';
  const prefix = input.lastSyncAt
    ? `${formatDistanceToNow(new Date(input.lastSyncAt), { addSuffix: true })} - `
    : '';
  if (input.lastSyncStatus === 'FAILED') {
    return `${prefix}Failed: ${input.lastSyncError ?? 'Unknown error'}`;
  }
  return `${prefix}${input.lastSyncStatus === 'SUCCESS' ? 'Success' : 'Pending'}`;
}

export function AutoSyncPanel({ account }: { account: AccountDto }) {
  const config = useFlexConfig(account.id);
  const saveConfig = useSaveFlexConfig();
  const disableConfig = useDisableFlexConfig();
  const testConnection = useTestFlexConnection();
  const syncNow = useSyncFlexNow();
  const [showToken, setShowToken] = useState(false);
  const [token, setToken] = useState('');
  const [tradeQueryId, setTradeQueryId] = useState('');
  const [navQueryId, setNavQueryId] = useState('');
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    if (!config.data) return;
    setTradeQueryId(config.data.tradeQueryId);
    setNavQueryId(config.data.navQueryId ?? '');
    setEnabled(config.data.enabled);
  }, [config.data]);

  const statusText = useMemo(
    () =>
      config.data
        ? lastSyncText(config.data)
        : config.isLoading
          ? 'Loading sync status'
          : 'Not configured',
    [config.data, config.isLoading],
  );

  function save() {
    saveConfig.mutate(
      {
        accountId: account.id,
        ibkrToken: token,
        tradeQueryId,
        navQueryId: navQueryId.trim() ? navQueryId.trim() : null,
        enabled,
      },
      {
        onSuccess: () => {
          setToken('');
          toast.success('Auto-sync settings saved.');
        },
      },
    );
  }

  function test() {
    testConnection.mutate(
      { token, queryId: tradeQueryId },
      { onSuccess: () => toast.success('IBKR Flex connection succeeded.') },
    );
  }

  function disable() {
    disableConfig.mutate(account.id, {
      onSuccess: () => {
        setEnabled(false);
        toast.success('Auto-sync disabled.');
      },
    });
  }

  function runSync() {
    syncNow.mutate(account.id, {
      onSuccess: () => toast.success('Flex sync started.'),
    });
  }

  const tokenPlaceholder = config.data?.tokenMasked ?? '64-character token';
  const savedTradeQueryId = config.data?.tradeQueryId ?? '';
  const savedNavQueryId = config.data?.navQueryId ?? '';
  const savedEnabled = config.data?.enabled ?? true;
  const tokenValue = token.trim();
  const navQueryValue = navQueryId.trim();
  const isDirty =
    tokenValue.length > 0 ||
    tradeQueryId.trim() !== savedTradeQueryId ||
    navQueryValue !== savedNavQueryId ||
    enabled !== savedEnabled;
  const canSave = isDirty && tokenValue.length > 0 && tradeQueryId.trim().length > 0;
  const canTest = canSave;
  const syncPending = config.data?.lastSyncStatus === 'PENDING';

  return (
    <Card
      header={
        <div>
          <h2 className="text-lg font-semibold">Auto-Sync</h2>
          <p className="text-sm text-text-muted">{account.name}</p>
        </div>
      }
    >
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`flex-token-${account.id}`}>IBKR token</Label>
          <div className="flex gap-2">
            <Input
              id={`flex-token-${account.id}`}
              type={showToken ? 'text' : 'password'}
              autoComplete="off"
              value={token}
              placeholder={tokenPlaceholder}
              onChange={(event) => setToken(event.target.value)}
            />
            <Button
              type="button"
              variant="secondary"
              aria-label={showToken ? 'Hide IBKR token' : 'Show IBKR token'}
              onClick={() => setShowToken((current) => !current)}
            >
              {showToken ? <EyeOff aria-hidden size={18} /> : <Eye aria-hidden size={18} />}
            </Button>
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor={`flex-trade-query-${account.id}`}>Trade Query ID</Label>
          <Input
            id={`flex-trade-query-${account.id}`}
            value={tradeQueryId}
            onChange={(event) => setTradeQueryId(event.target.value)}
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor={`flex-nav-query-${account.id}`}>NAV Query ID</Label>
          <Input
            id={`flex-nav-query-${account.id}`}
            value={navQueryId}
            onChange={(event) => setNavQueryId(event.target.value)}
          />
        </div>
        <label className="flex h-10 items-center justify-between gap-3 rounded border border-border bg-bg px-3 text-sm">
          <span className="font-medium">Enabled</span>
          <input
            type="checkbox"
            checked={enabled}
            aria-label="Enable Flex auto-sync"
            onChange={(event) => setEnabled(event.target.checked)}
          />
        </label>
      </div>

      <div className="mt-4 rounded border border-border bg-bg p-3 text-sm">
        <span className="font-medium">Last sync: </span>
        <span
          className={config.data?.lastSyncStatus === 'FAILED' ? 'text-danger' : 'text-text-muted'}
        >
          {statusText}
        </span>
      </div>

      {testConnection.isError ? (
        <p className="mt-3 text-sm text-danger">{errorText(testConnection.error)}</p>
      ) : null}
      {saveConfig.isError ? (
        <p className="mt-3 text-sm text-danger">{errorText(saveConfig.error)}</p>
      ) : null}
      {syncNow.isError ? (
        <p className="mt-3 text-sm text-danger">{errorText(syncNow.error)}</p>
      ) : null}

      <div className="mt-4 flex flex-col gap-2 sm:flex-row">
        <Button type="button" disabled={!canSave || saveConfig.isPending} onClick={save}>
          Save settings
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={!canTest || testConnection.isPending}
          onClick={test}
        >
          Test connection
        </Button>
        <Button
          type="button"
          variant="secondary"
          disabled={!config.data?.enabled || syncNow.isPending || syncPending}
          onClick={runSync}
        >
          {syncPending ? 'Sync pending' : 'Sync now'}
        </Button>
        <Button
          type="button"
          variant="destructive"
          disabled={!config.data || disableConfig.isPending}
          onClick={disable}
        >
          Disable
        </Button>
      </div>
    </Card>
  );
}
